import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CategoryIcon } from "@/components/z6/CategoryIcon";
import { AllergenIcon, ALLERGEN_NAMES } from "@/components/z6/AllergenIcon";
import { VegMark } from "@/components/z6/VegMark";
import { useMenuMeta } from "@/lib/menuMeta";

const Tile = ({ id, className = "h-8 w-8" }) => (
  <span className={`flex items-center justify-center rounded-[10px_10px_10px_2px] bg-white/95 p-1.5 shadow-[0_2px_8px_-4px_rgba(11,46,68,0.4)] ring-1 ring-[#0B2E44]/15 ${className}`}>
    <CategoryIcon k={id} tone="light" className="h-full w-full" />
  </span>
);

// One tappable icon per menu a dish belongs to; tap opens the menu's name and one-line meaning.
export const SectionIcons = ({ ids, testId }) => {
  const meta = useMenuMeta();
  const shown = ids.filter((id) => meta[id]);
  if (!shown.length) return null;
  const stop = (e) => e.stopPropagation();
  return (
    <span data-testid={testId} className="flex flex-wrap gap-1.5" onClick={stop} onPointerDown={stop} onKeyDown={stop}>
      {shown.map((id) => (
        <Popover key={id}>
          <PopoverTrigger asChild>
            <span role="button" tabIndex={0} aria-label={meta[id].name} title={meta[id].name} data-testid={`${testId}-${id}`} className="cursor-pointer"><Tile id={id} /></span>
          </PopoverTrigger>
          <PopoverContent data-testid={`${testId}-${id}-pop`} side="top" className="w-64 rounded-[16px_16px_16px_3px] border-[#0B2E44]/15 bg-white/95 p-4 backdrop-blur-md" onClick={stop}>
            <p className="font-display text-xl text-[#0B2E44]">{meta[id].name}</p>
            <p className="font-ui mt-1 text-[15px] leading-snug text-[#33566B]">{meta[id].intro}</p>
          </PopoverContent>
        </Popover>
      ))}
    </span>
  );
};

const Item = ({ children, label }) => <li className="font-ui flex items-center gap-2 text-[14px] text-[#0B2E44]">{children}<span>{label}</span></li>;

// Bottom-of-page key: every menu icon, the diet marks and the allergen icons, each named.
export const IconKey = ({ testId = "icon-key" }) => {
  const meta = useMenuMeta();
  return (
    <section data-testid={testId} className="mt-14 rounded-[22px_22px_22px_4px] border border-[#0B2E44]/10 bg-white/70 p-5 backdrop-blur-sm sm:p-6">
      <h2 className="font-display text-2xl text-[#0B2E44]">Key</h2>
      <p className="font-ui mt-4 text-[12px] uppercase tracking-[0.2em] text-[#B8326C]">Menus</p>
      <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">{Object.entries(meta).map(([id, m]) => <Item key={id} label={m.name}><Tile id={id} className="h-7 w-7" /></Item>)}</ul>
      <p className="font-ui mt-5 text-[12px] uppercase tracking-[0.2em] text-[#B8326C]">Diet</p>
      <ul className="mt-2 flex flex-wrap gap-x-6 gap-y-2"><Item label="Veg"><VegMark diet="vegetarian" /></Item><Item label="Non-veg"><VegMark diet="non-veg" /></Item><Item label="Vegan (marked Veg)"><VegMark diet="vegan" /></Item></ul>
      <p className="font-ui mt-5 text-[12px] uppercase tracking-[0.2em] text-[#B8326C]">Allergens</p>
      <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-4">
        {ALLERGEN_NAMES.map((a) => <Item key={a} label={a}><span className="flex h-7 w-7 items-center justify-center rounded-full border border-[#0B2E44]/15 bg-white"><AllergenIcon name={a} className="h-3.5 w-3.5" /></span></Item>)}
      </ul>
    </section>
  );
};
